import api from '@/lib/api';

export interface TemplateStrategy {
  id?: number;
  client_id: number;
  template_id: number;
  strategy_type: string;
  tag_code: string;
  days_before_due?: number;
  days_before_due_from?: number;
  days_before_due_to?: number;
  days_after_due?: number;
  days_after_due_from?: number;
  days_after_due_to?: number;
  is_active?: boolean;
  send_time?: string;
  max_attempts?: number;
  priority?: number;
  description?: string;
  business_rule?: string;
}

export interface CreateTemplateStrategyRequest {
  client_id: number;
  template_id: number;
  strategy_type: string;
  tag_code: string;
  days_before_due?: number;
  days_before_due_from?: number;
  days_before_due_to?: number;
  days_after_due?: number;
  days_after_due_from?: number;
  days_after_due_to?: number;
  is_active?: boolean;
  send_time?: string;
  max_attempts?: number;
  priority?: number;
  description?: string;
  business_rule?: string;
}

class TemplateStrategyService {
  private baseUrl = '/template-strategies';

  async createStrategy(data: CreateTemplateStrategyRequest): Promise<any> {
    console.log('🚀 [TemplateStrategy] Criando nova estratégia');
    console.log('🔧 [TemplateStrategy] Dados recebidos:', data);
    console.log('🔧 [TemplateStrategy] URL base:', this.baseUrl);
    console.log('🔧 [TemplateStrategy] URL completa:', `${this.baseUrl}`);
    
    try {
      const result = await api.post(this.baseUrl, data);
      console.log('✅ [TemplateStrategy] Estratégia criada com sucesso:', result);
      return result;
    } catch (error) {
      console.error('❌ [TemplateStrategy] Erro ao criar estratégia:', error);
      throw error;
    }
  }

  async getStrategiesByClient(clientId: number): Promise<any> {
    console.log('🚀 [TemplateStrategy] Buscando estratégias do cliente:', clientId);
    console.log('🔧 [TemplateStrategy] URL:', `${this.baseUrl}/client/${clientId}`);
    
    try {
      const result = await api.get(`${this.baseUrl}/client/${clientId}`);
      console.log('✅ [TemplateStrategy] Estratégias encontradas:', result);
      return result;
    } catch (error) {
      console.error('❌ [TemplateStrategy] Erro ao buscar estratégias:', error);
      throw error;
    }
  }

  async updateStrategy(id: number, data: Partial<TemplateStrategy>): Promise<any> {
    console.log('🚀 [TemplateStrategy] Atualizando estratégia:', id);
    console.log('🔧 [TemplateStrategy] Dados:', data);
    console.log('🔧 [TemplateStrategy] URL:', `${this.baseUrl}/${id}`);
    
    try {
      const result = await api.put(`${this.baseUrl}/${id}`, data);
      console.log('✅ [TemplateStrategy] Estratégia atualizada:', result);
      return result;
    } catch (error) {
      console.error('❌ [TemplateStrategy] Erro ao atualizar estratégia:', error);
      throw error;
    }
  }

  async deleteStrategy(id: number): Promise<void> {
    console.log('🚀 [TemplateStrategy] Excluindo estratégia:', id);
    console.log('🔧 [TemplateStrategy] URL:', `${this.baseUrl}/${id}`);
    
    try {
      await api.delete(`${this.baseUrl}/${id}`);
      console.log('✅ [TemplateStrategy] Estratégia excluída com sucesso');
    } catch (error) {
      console.error('❌ [TemplateStrategy] Erro ao excluir estratégia:', error);
      throw error;
    }
  }

  async validateClientConfiguration(clientId: number): Promise<any> {
    console.log('🚀 [TemplateStrategy] Validando configuração do cliente:', clientId);
    console.log('🔧 [TemplateStrategy] URL:', `${this.baseUrl}/validate/${clientId}`);
    
    try {
      const result = await api.get(`${this.baseUrl}/validate/${clientId}`);
      console.log('✅ [TemplateStrategy] Configuração validada:', result);
      return result;
    } catch (error) {
      console.error('❌ [TemplateStrategy] Erro ao validar configuração:', error);
      throw error;
    }
  }

  async getStrategiesByCriteria(clientId: number, criteria: any): Promise<any> {
    console.log('🚀 [TemplateStrategy] Buscando estratégias por critérios');
    console.log('🔧 [TemplateStrategy] Cliente:', clientId);
    console.log('🔧 [TemplateStrategy] Critérios:', criteria);
    console.log('🔧 [TemplateStrategy] URL:', `${this.baseUrl}/by-criteria/${clientId}`);
    
    try {
      const result = await api.post(`${this.baseUrl}/by-criteria/${clientId}`, criteria);
      console.log('✅ [TemplateStrategy] Estratégias encontradas por critérios:', result);
      return result;
    } catch (error) {
      console.error('❌ [TemplateStrategy] Erro ao buscar estratégias por critérios:', error);
      throw error;
    }
  }

  // Método para testar a conectividade da API
  async testConnection(): Promise<boolean> {
    console.log('🧪 [TemplateStrategy] Testando conectividade da API');
    console.log('🔧 [TemplateStrategy] URL de teste:', `${this.baseUrl}/client/1`);
    
    try {
      // Tentar fazer uma requisição simples para testar a conectividade
      const response = await fetch(`${this.baseUrl}/client/1`, {
        method: 'GET',
        credentials: 'include'
      });
      
      console.log('🔧 [TemplateStrategy] Status da resposta:', response.status);
      console.log('🔧 [TemplateStrategy] URL final da resposta:', response.url);
      
      // Se retornar 401 (não autorizado), significa que a rota existe mas precisa de autenticação
      if (response.status === 401) {
        console.log('✅ [TemplateStrategy] Rota existe e está funcionando (401 = não autorizado)');
        return true;
      }
      
      // Se retornar 200, significa que funcionou
      if (response.status === 200) {
        console.log('✅ [TemplateStrategy] Rota existe e funcionou perfeitamente');
        return true;
      }
      
      // Se retornar 404, significa que a rota não existe
      if (response.status === 404) {
        console.error('❌ [TemplateStrategy] Rota não encontrada (404)');
        return false;
      }
      
      console.log('⚠️ [TemplateStrategy] Status inesperado:', response.status);
      return false;
    } catch (error) {
      console.error('❌ [TemplateStrategy] Erro ao testar conectividade:', error);
      return false;
    }
  }

  // Método para debug da configuração da API
  debugApiConfiguration(): void {
    console.log('🔍 [TemplateStrategy] Debug da configuração da API');
    console.log('🔧 [TemplateStrategy] Base URL do serviço:', this.baseUrl);
    console.log('🔧 [TemplateStrategy] API_BASE_URL da lib:', (api as any).API_BASE_URL);
    console.log('🔧 [TemplateStrategy] URL atual da página:', window.location.href);
    console.log('🔧 [TemplateStrategy] Cookies disponíveis:', document.cookie);
    
    // Testar se a variável de ambiente está sendo lida corretamente
    console.log('🔧 [TemplateStrategy] VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
    console.log('🔧 [TemplateStrategy] Ambiente:', import.meta.env.MODE);
    console.log('🔧 [TemplateStrategy] Dev mode:', import.meta.env.DEV);
  }
}

const templateStrategyService = new TemplateStrategyService();
export default templateStrategyService;
